// =====================================================================
// EGL v12 Frontend - Excel Export Utility
// Purpose: Export certificate list to Excel format
// =====================================================================

import { CertificateListItem, ColumnConfig } from '../types/certificateList';

/**
 * Export certificates to Excel format
 * Uses a simple CSV approach that Excel can open
 */
export const exportToExcel = (
  certificates: CertificateListItem[],
  columns: ColumnConfig[],
  filename: string = 'certificates.csv'
) => {
  if (certificates.length === 0) {
    alert('No certificates to export');
    return;
  }

  // Filter enabled columns
  const enabledColumns = columns.filter(col => col.enabled);
  
  if (enabledColumns.length === 0) {
    alert('Please select at least one column to export');
    return;
  }

  // Build CSV header
  const headers = enabledColumns.map(col => col.label).join(',');
  
  // Build CSV rows
  const rows = certificates.map((cert, index) => {
    return enabledColumns.map(col => {
      let value: any;
      
      // Handle special columns
      if (col.key === 'index') {
        value = index + 1;
      } else if (col.key === 'public_url') {
        const baseUrl = window.location.origin;
        value = `${baseUrl}/${cert.uurlid}`;
      } else if (col.key === 'created_at') {
        // Format date
        const date = new Date(cert[col.key as keyof CertificateListItem] as string);
        value = date.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        value = cert[col.key as keyof CertificateListItem] || '';
      }
      
      // Escape CSV special characters
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      
      return stringValue;
    }).join(',');
  });
  
  // Combine header and rows
  const csvContent = [headers, ...rows].join('\n');
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Advanced Excel export using SheetJS (if available)
 * Falls back to CSV if library not available
 */
export const exportToExcelAdvanced = async (
  certificates: CertificateListItem[],
  columns: ColumnConfig[],
  filename: string = 'certificates.xlsx'
) => {
  try {
    // Try to use SheetJS if available (would need to be added to package.json)
    const XLSX = (window as any).XLSX;
    
    if (!XLSX) {
      // Fallback to CSV export
      console.log('SheetJS not available, using CSV export');
      return exportToExcel(certificates, columns, filename.replace('.xlsx', '.csv'));
    }
    
    // Filter enabled columns
    const enabledColumns = columns.filter(col => col.enabled);
    
    // Build worksheet data
    const wsData = [
      // Header row
      enabledColumns.map(col => col.label),
      // Data rows
      ...certificates.map((cert, index) => {
        return enabledColumns.map(col => {
          if (col.key === 'index') return index + 1;
          if (col.key === 'public_url') return `${window.location.origin}/${cert.uurlid}`;
          if (col.key === 'created_at') {
            const date = new Date(cert[col.key as keyof CertificateListItem] as string);
            return date.toLocaleString('en-GB');
          }
          return cert[col.key as keyof CertificateListItem] || '';
        });
      })
    ];
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Auto-size columns
    const colWidths = enabledColumns.map((col, i) => {
      const maxLength = Math.max(
        col.label.length,
        ...wsData.slice(1).map(row => String(row[i] || '').length)
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    ws['!cols'] = colWidths;
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Certificates');
    
    // Download
    XLSX.writeFile(wb, filename);
    
  } catch (error) {
    console.error('Excel export error:', error);
    // Fallback to CSV
    exportToExcel(certificates, columns, filename.replace('.xlsx', '.csv'));
  }
};

